import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { useCreateClassCountTuitionBilling } from '~entities/payment/api/class-count-tuition/create-class-count-tuition-billing';
import { type ClassCountTuitionBilling } from '~entities/payment/api/class-count-tuition/get-class-count-tuition-billing';
import { useUpdateClassCountTuitionBilling } from '~entities/payment/api/class-count-tuition/update-class-count-tuition-billing';

import { Button } from '~shared/shadcn/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';

const rangeSchema = z
  .object({
    minClasses: z.number().int().min(1, 'Min must be at least 1'),
    maxClasses: z.number().int().min(1, 'Max must be at least 1'),
    flatRate: z.number().min(0, 'Rate must be at least 0'),
  })
  .refine((range) => range.maxClasses >= range.minClasses, {
    message: 'Max must be ≥ min',
    path: ['maxClasses'],
  });

const formSchema = z.object({
  name: z.string().min(1, 'Chart name is required'),
  ranges: z
    .array(rangeSchema)
    .min(1, 'Add at least one band')
    .refine(
      (ranges) => {
        const sorted = [...ranges].sort((a, b) => a.minClasses - b.minClasses);
        for (let i = 1; i < sorted.length; i++) {
          if (sorted[i]!.minClasses <= sorted[i - 1]!.maxClasses) return false;
        }
        return true;
      },
      { message: 'Bands must not overlap and should ascend by class count' },
    ),
});

type FormValues = z.infer<typeof formSchema>;

const SAMPLE_FAMILY_RATES_PRESET: FormValues = {
  name: 'Family class count',
  ranges: [
    { minClasses: 1, maxClasses: 1, flatRate: 86 },
    { minClasses: 2, maxClasses: 2, flatRate: 162 },
  ],
};

interface ClassCountChartFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  chart?: ClassCountTuitionBilling | null;
}

export const ClassCountChartFormDialog = ({
  open,
  onOpenChange,
  chart = null,
}: ClassCountChartFormDialogProps) => {
  const isEdit = !!chart;
  const { mutateAsync: createChart, isPending: isCreating } =
    useCreateClassCountTuitionBilling();
  const { mutateAsync: updateChart, isPending: isUpdating } =
    useUpdateClassCountTuitionBilling();
  const isPending = isCreating || isUpdating;

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: SAMPLE_FAMILY_RATES_PRESET,
    mode: 'onBlur',
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ranges',
  });

  useEffect(() => {
    if (!open) return;
    if (chart) {
      reset({
        name: chart.name,
        ranges: chart.ranges.map((range) => ({
          minClasses: range.minClasses,
          maxClasses: range.maxClasses,
          flatRate: range.flatRate,
        })),
      });
      return;
    }
    reset(SAMPLE_FAMILY_RATES_PRESET);
  }, [open, chart, reset]);

  const addBand = () => {
    const last = fields[fields.length - 1];
    const nextMin = last?.maxClasses ? last.maxClasses + 1 : 1;
    append({ minClasses: nextMin, maxClasses: nextMin, flatRate: 0 });
  };

  const onSubmit = async (values: FormValues) => {
    try {
      if (isEdit && chart) {
        await updateChart({ chartId: chart._id, params: values });
        toast.success('Rate chart updated');
      } else {
        await createChart(values);
        toast.success('Rate chart created');
      }
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Could not save rate chart',
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[90vh] max-w-xl overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit rate chart' : 'New rate chart'}
          </DialogTitle>
          <DialogDescription>
            Family monthly total from how many classes the household holds.
            Siblings split that total equally.
          </DialogDescription>
        </DialogHeader>

        <form className='space-y-5' onSubmit={handleSubmit(onSubmit)}>
          <div className='space-y-2'>
            <Label htmlFor='chart-name'>Name</Label>
            <Input
              id='chart-name'
              placeholder='e.g. Family monthly rates'
              {...register('name')}
            />
            {errors.name && (
              <p className='text-sm text-destructive'>{errors.name.message}</p>
            )}
          </div>

          <div className='space-y-3'>
            <div className='flex items-end justify-between gap-3'>
              <div>
                <Label>Class-count bands</Label>
                <p className='text-xs text-muted-foreground'>
                  Each band is a flat monthly family total for that class count.
                </p>
              </div>
              {!isEdit && (
                <Button
                  type='button'
                  variant='outline'
                  size='sm'
                  onClick={() => {
                    reset(SAMPLE_FAMILY_RATES_PRESET);
                  }}
                >
                  Load sample rates
                </Button>
              )}
            </div>

            <div className='overflow-hidden rounded-lg border'>
              <div className='grid grid-cols-[1fr_1fr_1fr_auto] gap-2 border-b bg-muted/40 px-3 py-2 text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                <span>Min classes</span>
                <span>Max classes</span>
                <span>Family total ($)</span>
                <span className='w-8' />
              </div>
              <div className='divide-y'>
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className='grid grid-cols-[1fr_1fr_1fr_auto] items-start gap-2 px-3 py-2'
                  >
                    <div>
                      <Input
                        type='number'
                        min={1}
                        {...register(`ranges.${index}.minClasses`, {
                          valueAsNumber: true,
                        })}
                      />
                      {errors.ranges?.[index]?.minClasses && (
                        <p className='mt-1 text-xs text-destructive'>
                          {errors.ranges[index]?.minClasses?.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Input
                        type='number'
                        min={1}
                        {...register(`ranges.${index}.maxClasses`, {
                          valueAsNumber: true,
                        })}
                      />
                      {errors.ranges?.[index]?.maxClasses && (
                        <p className='mt-1 text-xs text-destructive'>
                          {errors.ranges[index]?.maxClasses?.message}
                        </p>
                      )}
                    </div>
                    <div>
                      <Input
                        type='number'
                        min={0}
                        step='0.01'
                        {...register(`ranges.${index}.flatRate`, {
                          valueAsNumber: true,
                        })}
                      />
                      {errors.ranges?.[index]?.flatRate && (
                        <p className='mt-1 text-xs text-destructive'>
                          {errors.ranges[index]?.flatRate?.message}
                        </p>
                      )}
                    </div>
                    <Button
                      type='button'
                      variant='ghost'
                      size='icon'
                      className='mt-0.5 text-destructive'
                      disabled={fields.length <= 1}
                      onClick={() => {
                        remove(index);
                      }}
                    >
                      <Trash2 className='size-4' />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
            {typeof errors.ranges?.message === 'string' && (
              <p className='text-sm text-destructive'>{errors.ranges.message}</p>
            )}
            <Button type='button' variant='outline' size='sm' onClick={addBand}>
              <Plus className='mr-1 size-4' />
              Add band
            </Button>
          </div>

          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={() => {
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
            <Button type='submit' disabled={isPending}>
              {isPending && <Loader2 className='mr-2 size-4 animate-spin' />}
              {isEdit ? 'Save changes' : 'Create chart'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
